import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import RouteWrapper from './RouteWrapper';
import Error from '@/components/Error';

// Not lazy: a lazy chunk would mount in a later commit than logout's cleanup effect,
// letting a stale redirect slip through.
import UnauthenticatedRedirect from './UnauthenticatedRedirect';

import {
  LOGIN,
  SSO_LOGIN,
  SIGNUP,
  ICONLIST,
  FORGOT_PASSWORD,
  SSO_CALLBACK,
  STATUS_CHECK,
  ZOOM_INTEGRATION_GUIDE,
  ATS_AUTH_GUIDE,
  CONSENT,
  GO,
} from './routes';

const PublicLayout = lazy(() => import('@/core/public'));
const Login = lazy(() => import('@/core/public/Login'));
const SSOLogin = lazy(() => import('@/core/public/SSOLogin'));
const SignUp = lazy(() => import('@/core/public/Signup'));
const Icons = lazy(() => import('@/core/public/IconList'));
const ForgotPassword = lazy(() => import('@/core/public/ForgotPassword'));
const StatusCheck = lazy(() => import('@/core/public/StatusCheck'));
const SSOCallback = lazy(() => import('@/core/public/SSOCallback'));
const ZoomIntegrationGuide = lazy(() => import('@/core/public/ZoomIntegrationGuide'));
const AtsAuthGuidePage = lazy(() => import('@/core/protected/Integrations/Ats/AtsAuthGuidePage'));
const Consent = lazy(() => import('@/core/public/Consent'));
const GoRoute = lazy(() => import('./GoRoute'));
// const PasswordReset = lazy(() => import('@/core/public/PasswordReset'));
// const SetPassword = lazy(() => import('@/core/public/SetPassword'));
// const OtpInput = lazy(() => import('@/core/public/OTP'));

export type CustomRouteObject = RouteObject & {
  title?: string;
  children?: CustomRouteObject[];
};

const publicRoutes = [
  {
    // Candidate consent page — top-level, outside the login-branded PublicLayout.
    path: CONSENT,
    element: (
      <RouteWrapper>
        <Consent />
      </RouteWrapper>
    ),
  },
  {
    path: '/',
    element: (
      <RouteWrapper>
        <PublicLayout />
      </RouteWrapper>
    ),
    errorElement: (
      <Error statusCode={404} title="Page Not Found" message="The page you are looking for does not exist." />
    ),
    children: [
      {
        index: true,
        element: <Navigate to={LOGIN} replace />,
      },
      {
        path: LOGIN,
        title: 'Login',
        element: <Login />,
      },
      {
        path: SSO_LOGIN,
        title: 'Login with SSO',
        element: <SSOLogin />,
      },
      {
        path: SIGNUP,
        title: 'Sign Up',
        element: <SignUp />,
      },
      // {
      //   path: RESET_PASSWORD,
      //   title: 'Reset Password',
      //   element: <PasswordReset />,
      // },

      {
        path: ICONLIST,
        title: 'Icon List',
        element: <Icons />,
      },
      {
        path: FORGOT_PASSWORD,
        title: 'Forgot Password',
        element: <ForgotPassword />,
      },
      {
        path: SSO_CALLBACK,
        title: 'Signing in',
        element: <SSOCallback />,
      },
      // {
      //   path: SET_PASSWORD,
      //   title: 'Set Password',
      //   element: <SetPassword />,
      // },
      // {
      //   path: OTP,
      //   title: 'OTP',
      //   element: <OtpInput />,
      // },
    ],
  },
  {
    path: STATUS_CHECK,
    title: 'Status Check',
    element: (
      <RouteWrapper>
        <StatusCheck />
      </RouteWrapper>
    ),
  },
  {
    path: GO,
    title: 'Redirecting',
    element: (
      <RouteWrapper>
        <GoRoute />
      </RouteWrapper>
    ),
  },
  {
    path: ZOOM_INTEGRATION_GUIDE,
    title: 'Connecting Zoom',
    element: (
      <RouteWrapper>
        <ZoomIntegrationGuide />
      </RouteWrapper>
    ),
  },
  {
    path: ATS_AUTH_GUIDE,
    title: 'ATS Authorization Guide',
    element: (
      <RouteWrapper>
        <AtsAuthGuidePage />
      </RouteWrapper>
    ),
  },
  {
    path: '*',
    title: 'Redirecting',
    element: (
      <RouteWrapper>
        <UnauthenticatedRedirect />
      </RouteWrapper>
    ),
  },
];

export default publicRoutes as CustomRouteObject[];
